// OrderMessages.jsx - The thread with one customer, and the box to reply.
//
// Surge is the source of truth for texts and it has its own inbox UI, which the
// inbox screen embeds. This view is the local mirror, so an order page shows its
// own conversation without waiting on a third party request.
//
// A reply arriving lands here without a refresh: lib/live.js pushes it, and this
// view reloads the order when the message belongs to this one.
//
// LEGEND
//   1. Bubble      one message
//   2. Composer    reply by text or email
//   3. Resend      send a customer template again

import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { ordersApi } from "../lib/api.js";
import { useLiveOrPoll } from "../lib/live.js";
import { dayTime, ago, phone } from "../lib/format.js";
import { Alert, Button, Empty, Input, Panel, Select, Tag, Textarea, useToast } from "../ui/kit.jsx";
import { Message, Envelope, Refresh } from "../ui/icons.jsx";

////////// 1. BUBBLE //////////

function Bubble({ message }) {
  const outbound = message.direction === "outbound";
  const failed = message.status === "failed";
  // The mail server said try again later. The sweep in lib/mailer.js will, and
  // the reason is on hover for whoever wonders why it has not gone yet.
  const retrying = message.status === "retrying";

  return (
    <li className={clsx("flex", outbound ? "justify-end" : "justify-start")}>
      <div className={clsx("max-w-[80%] min-w-0", outbound ? "text-right" : "text-left")}>
        <div className={clsx(
          "inline-block px-3.5 py-2.5 text-[14px] leading-relaxed text-left whitespace-pre-line rounded-sm border",
          outbound ? "bg-surface border-rule" : "bg-card border-rule",
          failed && "border-critical bg-critical-wash"
        )}>
          {message.subject && <span className="block font-semibold text-[13px] mb-1">{message.subject}</span>}
          {message.body}
        </div>
        <p className="text-[11.5px] text-faint mt-1 flex items-center gap-1.5 justify-end">
          {message.channel === "email" ? <Envelope size={12} /> : <Message size={12} />}
          {message.template ? <span className="text-faint">{message.template.replace(/_/g, " ")}</span> : null}
          <span>{dayTime(message.createdAt)}</span>
          {failed && <span className="text-critical font-semibold">failed</span>}
          {retrying && <span className="text-caution font-semibold" title={message.errorMessage || ""}>trying again</span>}
          {message.status === "delivered" && <span className="text-positive">delivered</span>}
        </p>
      </div>
    </li>
  );
}

////////// 2. COMPOSER //////////

const TEMPLATES = [
  ["tracking_link", "Send their tracking link"],
  ["items_received", "We have your package"],
  ["offer_ready", "Your offer is ready"],
  ["kit_shipped", "Your kit is on the way"],
];

// Matches the sources the server knows how to word. See CONSENT_WORDING in
// server/src/routes/orders.js, which writes the sentence that is actually kept.
const CONSENT_SOURCES = [
  { value: "in_person", label: "In person" },
  { value: "phone", label: "Over the phone" },
  { value: "paper", label: "On a paper form" },
  { value: "email", label: "In writing, by email" },
  { value: "other", label: "Some other way" },
];

export default function OrderMessages({ order, onChanged }) {
  const toast = useToast();
  const [channel, setChannel] = useState(order.consent?.sms ? "sms" : "email");
  const [consentSource, setConsentSource] = useState("in_person");
  const [recording, setRecording] = useState(false);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);

  const messages = [...(order.messages || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const thread = useRef(null);

  // Only reload for a message on this order. A busy Saturday should not make
  // every open order page refetch every time anybody texts us.
  const onLive = useCallback((payload) => {
    if (payload && payload.orderNumber && payload.orderNumber !== order.number) return;
    onChanged();
  }, [order.number, onChanged]);

  useLiveOrPoll(["message.inbound", "message.status"], onLive, { interval: 12_000 });

  // A new message at the bottom of a scrolled thread is a message nobody sees.
  useEffect(() => {
    const box = thread.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages.length]);

  // Recording what somebody was actually told, rather than ticking a box. The
  // wording that ends up on the order is written by the server; this only says
  // which of them it is.
  const recordConsent = async () => {
    setRecording(true);
    try {
      await ordersApi.update(order.number, { consent: { sms: true, source: consentSource } });
      toast.success("Consent recorded");
      setChannel("sms");
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRecording(false);
    }
  };

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await ordersApi.sendMessage(order.number, { channel, body, subject: subject || undefined });
      toast.success(channel === "sms" ? "Text sent" : "Email sent");
      setBody("");
      setSubject("");
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async (template) => {
    setBusy(true);
    try {
      const answer = await ordersApi.resend(order.number, template);
      // Said as it happened. This used to say "Sent" whatever came back,
      // including an email that never left the building.
      const email = answer?.results?.email;
      if (email && !email.ok) toast.error(`Not sent: ${email.reason}`);
      else if (email?.queued) toast.success("The mail server asked us to wait. It goes out on the next try.");
      else toast.success("Sent");
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="space-y-4">
        <Panel className="p-4">
          {messages.length ? (
            <ul ref={thread} className="space-y-4 max-h-[min(55dvh,520px)] overflow-y-auto overscroll-contain pr-1">
              {messages.map((message) => <Bubble key={message._id} message={message} />)}
            </ul>
          ) : (
            <Empty title="Nothing sent yet" icon={Message} className="py-10">
              Automatic messages appear here as the order moves.
            </Empty>
          )}
        </Panel>

        <Panel className="p-4">
          <div className="flex flex-wrap items-center gap-1 mb-3">
            <button
              type="button"
              onClick={() => setChannel("sms")}
              className={clsx("btn btn-sm", channel === "sms" ? "btn-ink" : "btn-quiet")}
            >
              <Message size={14} /> Text
            </button>
            <button
              type="button"
              onClick={() => setChannel("email")}
              className={clsx("btn btn-sm", channel === "email" ? "btn-ink" : "btn-quiet")}
            >
              <Envelope size={14} /> Email
            </button>
            <span className="hint self-center ml-2 num">
              {channel === "sms" ? phone(order.customer.phone) : order.customer.email}
            </span>
          </div>

          {channel === "sms" && !order.consent?.sms && (
            <Alert tone="warning" className="mb-3">
              <p className="font-semibold">No text consent on file for this customer.</p>
              <p className="mt-1 leading-snug">
                You can still send. It will be recorded as sent without a consent on file, with your name
                on it. If they did agree, say where and it becomes part of the order.
              </p>
              <div className="flex flex-wrap items-end gap-2 mt-3">
                <Select
                  label="They agreed"
                  value={consentSource}
                  onChange={(event) => setConsentSource(event.target.value)}
                  options={CONSENT_SOURCES}
                  wrapClassName="w-full xs:w-52"
                />
                <Button variant="quiet" size="sm" onClick={recordConsent} loading={recording}>
                  Record consent
                </Button>
              </div>
            </Alert>
          )}

          {channel === "email" && (
            <Input
              label="Subject"
              placeholder={`About your order ${order.number}`}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              wrapClassName="mb-3"
            />
          )}

          <Textarea
            rows={4}
            placeholder={channel === "sms" ? "Keep it short. Texts over 160 characters split into several." : "Write your message."}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <p className="hint num">
              {channel === "sms" ? `${body.length} characters, ${Math.max(1, Math.ceil(body.length / 160))} segments` : ""}
            </p>
            <Button
              onClick={send}
              loading={busy}
              disabled={!body.trim()}
              className="max-xs:w-full ml-auto"
            >
              {channel === "sms" && !order.consent?.sms ? "Send anyway" : "Send"}
            </Button>
          </div>
        </Panel>
      </div>

      {/* Resend automated messages */}
      <div className="space-y-4">
        <Panel className="p-4">
          <p className="eyebrow mb-3">Send a standard message</p>
          <ul className="space-y-1.5">
            {TEMPLATES.map(([key, label]) => (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => resend(key)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 text-[13.5px] text-ink-soft hover:bg-surface hover:text-ink transition-colors rounded-xs flex items-center gap-2"
                >
                  <Refresh size={14} className="text-faint shrink-0" /> {label}
                </button>
              </li>
            ))}
          </ul>
          <p className="hint mt-3 pt-3 border-t border-rule-soft leading-relaxed">
            Goes out by email, and by text as well if they consented. The exact wording is in the server
            templates so it never drifts.
          </p>
        </Panel>

        <Panel quiet className="p-4">
          <p className="eyebrow mb-2">Consent</p>
          <Tag tone={order.consent?.sms ? "positive" : "neutral"}>
            {order.consent?.sms ? "Texts allowed" : "No text consent"}
          </Tag>
          {order.consent?.smsAt && <p className="hint mt-2">Given {ago(order.consent.smsAt)}</p>}
          <p className="hint mt-2 leading-relaxed">
            A customer replying STOP to any text revokes this automatically and permanently.
          </p>
        </Panel>
      </div>
    </div>
  );
}
